/**
 * Extract plain text from Word documents (.docx / .doc) for knowledgebase indexing.
 * Uses mammoth (same approach as Owlbert Electron local directory import).
 */

import mammoth from 'mammoth'

export type WordExtractResult =
  | { ok: true; text: string }
  | { ok: false; error: 'word_extract_failed' }

function toArrayBuffer(bytes: Uint8Array | ArrayBuffer): ArrayBuffer {
  if (bytes instanceof ArrayBuffer) return bytes
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

export async function extractWordText(bytes: Uint8Array | ArrayBuffer): Promise<WordExtractResult> {
  try {
    const arrayBuffer = toArrayBuffer(
      bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes,
    )
    const result = await mammoth.extractRawText({ arrayBuffer })
    const text = result.value?.trim() ?? ''
    return { ok: true, text: text || '(No extractable text)' }
  } catch {
    return { ok: false, error: 'word_extract_failed' }
  }
}

export type WordHtmlResult =
  | { ok: true; html: string }
  | { ok: false; error: 'word_html_failed' }

/**
 * Convert a Word document to styled HTML (headings, bold, lists, tables) for preview.
 * Best-effort: never throws. Used only for display, not for KB indexing.
 */
export async function extractWordHtml(bytes: Uint8Array | ArrayBuffer): Promise<WordHtmlResult> {
  try {
    const arrayBuffer = toArrayBuffer(
      bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes,
    )
    const result = await mammoth.convertToHtml({ arrayBuffer })
    const html = result.value?.trim() ?? ''
    return { ok: true, html }
  } catch {
    return { ok: false, error: 'word_html_failed' }
  }
}
